import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaFlask, FaChartLine } from "react-icons/fa";
import "./StateSyllabus.css";
import "./PUCollege.css";
import { PU_ENQUIRY_URL } from "../config";

const PUCollege = () => {
  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "2010",
      description: "Sri Vani has offered PU College since 2010",
    },
    {
      icon: "📋",
      title: "Affiliated To",
      value: "Karnataka PU Board",
      description: "Department of Pre-University Education, Karnataka",
    },
    {
      icon: "🎯",
      title: "Programs",
      value: "Integrated",
      description: "Robust integrated programs for PU & competitive exams",
    },
    {
      icon: "🏫",
      title: "Campus",
      value: "Machohalli",
      description: "Available at Machohalli Campus with transport facility",
    },
  ];

  const scienceCombinations = [
    {
      name: "PCMB",
      full: "Physics, Chemistry, Mathematics & Biology",
      tag: "Medical Track",
      integrated: ["Science + NEET", "Science + CET"],
    },
    {
      name: "PCMC",
      full: "Physics, Chemistry, Mathematics & Computer Science",
      tag: "Engineering Track",
      integrated: ["Science + JEE", "Science + CET"],
    },
  ];

  const commerceCombinations = [
    {
      name: "SEBA",
      full: "Statistics, Economics, Business Studies & Accountancy",
      integrated: ["Commerce + CA Foundation"],
    },
    {
      name: "CEBA",
      full: "Computer Science, Economics, Business Studies & Accountancy",
      integrated: ["Commerce + CA Foundation"],
    },
    {
      name: "BASBm",
      full: "Business Studies, Accountancy, Statistics & Basic Mathematics",
      integrated: ["Commerce + CA Foundation"],
    },
  ];

  const campus = {
    name: "Machohalli Campus",
    subtitle: "Macholli",
    tagline: "A Premier Centre of Academic Excellence & Innovation.",
    image: "/images/pu/pu-campus.jpg",
    link: "/campuses/macholli",
  };

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/pu/pu-hero.png')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Karnataka Pre-University Board</p>
            <h1>PU College</h1>
            <p className="ss-header-sub">Sri Vani Vidya Kendra PU College</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/pu/pu-badge.png"
                alt="Sri Vani PU College Badge"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 2010</li>
                <li>Affiliated to Karnataka PU Board</li>
                <li>Robust Integrated Programs</li>
                <li>Pursue at Machohalli Campus</li>
                <li>Transport Facility Available</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              After 10 years of schooling, a young mind hitherto sheltered in a safe
              space is usually thrust into the chaotic uncertainties of the college
              world. Sri Vani PU College bridges that gap with a structured, nurturing
              environment that prepares students for both board exams and life beyond.
            </p>
            <p>
              The college provides a structured integrated program that prepares
              students for both PU board exams and other competitive exams including
              CET, NEET, JEE, and CA Foundation — all under one roof.
            </p>
            <div className="ss-desc-cta">
              <a
                href={PU_ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Streams Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">PUC Streams &amp; Combinations</h2>
            <p className="ss-section-subtitle">
              Choose your stream and build your future — Classes XI &amp; XII
            </p>
          </motion.div>

          {/* Science Stream */}
          <motion.div
            className="pu-stream-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="pu-stream-header science">
              <FaFlask />
              <h3>Science Stream</h3>
            </div>
            <div className="pu-combinations-grid">
              {scienceCombinations.map((combo, index) => (
                <div key={index} className="pu-combo-card">
                  <div className="pu-combo-name">{combo.name}</div>
                  <p className="pu-combo-full">{combo.full}</p>
                  {combo.tag && <span className="pu-combo-tag">{combo.tag}</span>}
                  <div className="pu-integrated">
                    <p className="pu-integrated-label">Integrated Tracks:</p>
                    <ul>
                      {combo.integrated.map((track, i) => (
                        <li key={i}>{track}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Commerce Stream */}
          <motion.div
            className="pu-stream-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="pu-stream-header commerce">
              <FaChartLine />
              <h3>Commerce Stream</h3>
            </div>
            <div className="pu-combinations-grid three-col">
              {commerceCombinations.map((combo, index) => (
                <div key={index} className="pu-combo-card">
                  <div className="pu-combo-name">{combo.name}</div>
                  <p className="pu-combo-full">{combo.full}</p>
                  <div className="pu-integrated">
                    <p className="pu-integrated-label">Integrated Track:</p>
                    <ul>
                      {combo.integrated.map((track, i) => (
                        <li key={i}>{track}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Vani Vitta Nidhi callout */}
          <motion.div
            className="ss-subject-note pu-callout"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p>
              <strong>Vani Vitta Nidhi</strong> — Commerce learners gain access to
              our one-of-a-kind platform designed to develop the professional skills
              and tactics needed to take the current business world by the horns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Campus Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at Machohalli Campus
            </div>
            <h2 className="ss-section-title">Our PU College Campus</h2>
          </motion.div>

          <div className="cbse-campus-center">
            <motion.div
              className="ss-campus-card cbse-single-campus"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="ss-campus-image">
                <img src={campus.image} alt={campus.name} />
                <div className="ss-campus-overlay">
                  <span>{campus.subtitle}</span>
                </div>
              </div>
              <div className="ss-campus-info">
                <h3>{campus.name}</h3>
                <p>{campus.tagline}</p>
                <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                  Explore Campus
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your PU Journey at Sri Vani</h2>
            <p>
              Integrated coaching for board exams and competitive entrance tests
              — all under one roof.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={PU_ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PUCollege;
